/**
 * `GET /v1/communities/{id}/insights` — group H, the aggregate route.
 * `docs/api-v1/04-v1-surface.md` §3.1 H, §4.3, §5.2.
 *
 * ONE ROUTE, TWENTY-THREE BLOCKS, AND YOU PAY FOR WHAT YOU NAME. `?include=` is
 * REQUIRED on the wire (omitting it is `400 unknown_include`, deliberately, so a
 * forgotten parameter cannot trigger a full scan), and nothing is computed for a
 * block nobody asked for. That is the whole of §5.2: the Overview reads exactly
 * nine numbers off the contributor census, and today it gets them from
 * `/admin/analytics/contributors`, a response that also carries up to 1,500
 * scatter points, the full cohort grid, `role_volume` and `quiet_experts`. Here
 * the Overview asks for `census,census_spark` and the scatter is never built.
 *
 * WHICH PAGE ASKS FOR WHAT (the three lists live at the bottom of this file, so
 * a page never spells a block name inline and two pages cannot drift):
 *
 *     OVERVIEW_BLOCKS      Overview        one request, replacing three
 *     CONTRIBUTOR_BLOCKS   Contributors    census + the heavy blocks it renders
 *     ENGAGEMENT_BLOCKS    Engagement      labels + timing + the clock
 *
 * `?last=` IS CACHE-KEY STABLE AND THAT IS WHY IT EXISTS (§1.4). The server caches
 * per `(skool_id, block, window)` with the window floored to the UTC day; a client
 * that computed `since` itself would send a microsecond-precise instant and miss
 * the cache on every request. Send `last`, never `since`, from an analytics page.
 * `?until=` is a 400 here — every aggregate under this route takes a lower bound
 * only, and accepting an upper one and ignoring it is the silence §1.6 removes.
 *
 * WHAT THE WINDOW DOES NOT MOVE. `census`, `scatter`, `role_volume`, `summary`'s
 * active/dormant read and `engagement` all classify over the FIXED 30-day
 * contributor window (`domain.contributors.ROLE_WINDOW_DAYS`) whatever `last` says,
 * so a badge, a dot and a census card can never disagree because two screens picked
 * different periods. The window sets the axis for the historical series only:
 * `heatmap`, `clock`, `trend`, `labels`, `timing`, `census_spark`, `top_posts`.
 *
 * SHAPES. Author cards are `MemberRef` and they are NESTED (`row.member.name`),
 * never flattened into the row, and an unresolvable author is `null` rather than a
 * fake member (§2.1) — so every consumer here filters nulls out rather than reading
 * through a placeholder. Instants carry a `+00:00` offset; parse them with
 * `parseTs`/`timeAgo` from `lib/admin`, never `new Date(raw)`.
 */

import { apiGet } from './http';
import { community } from './envelope';
import type { CommunityRef } from './envelope';

import type {
  AiFeedbackComment,
  AiSource,
  CommentBrief,
  PostBrief,
  TopContent,
} from './shapes';
import { toAuthor } from './posts';

import type {
  ClockCell,
  CohortRow,
  HeatmapDay,
  LabelStat,
  MemberStage,
  OverwhelmWeek,
} from './shapes';

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

/** §2.1's one author card, as it appears inside every row of every block here.
 *  `handle` is nullable in v1 (the legacy `PostAuthor` types it as a bare string
 *  and fabricates `"unknown"`), so anything rendering it must tolerate null. */
export interface InsightsMember {
  member_id: number;
  external_id: string;
  handle: string | null;
  name: string;
  picture_profile: string | null;
}

/** A post, as §2.2's `PostItem`. `top_posts` carries the always-present fields
 *  only — no `excerpt`, no `content`, no media. */
export interface InsightsPost {
  id: number;
  external_id: string;
  title: string | null;
  slug: string | null;
  url: string | null;
  author: InsightsMember | null;
  pinned: boolean;
  upvotes: number | null;
  comment_count: number | null;
  created_at: string | null;
}

// ---------------------------------------------------------------------------
// The blocks
// ---------------------------------------------------------------------------

/** CURRENT population throughout — headcount, contributing, active, lurkers and
 *  dormant all describe who is here today, not who ever posted. */
export interface SummaryBlock {
  total_posts: number;
  total_comments: number;
  total_members: number | null;
  contributing_members: number;
  lurkers: number;
  participation_rate: number | null;
  lurker_rate: number | null;
  active_members: number;
  active_rate: number | null;
  dormant: number;
  avg_comments_per_post: number | null;
}

/** Unanswered posts and time-to-first-reply, over the fixed 30-day window. */
export interface EngagementBlock {
  unanswered_posts: number;
  unanswered_rate: number | null;
  median_first_response_hours: number | null;
  answered_within_24h_rate: number | null;
}

/** How concentrated contribution is, over LIFETIME activity — departed members are
 *  kept here on purpose: dropping the people who left would flatten the very
 *  concentration this measures. */
export interface ConcentrationBlock {
  top10_share: number | null;
  gini: number | null;
}

/** One week of the activity trend. `roles` splits the week by the contributor role
 *  each member held AT THE TIME. */
export interface TrendWeek {
  week: string;
  posts: number;
  comments: number;
  engaged?: number;
  roles?: Record<string, number>;
}

export interface HeatmapBlock {
  days: HeatmapDay[];
  max: number;
}

/** One row of `momentum.warming` / `momentum.cooling`. Nested member, and every
 *  field but the card is nullable because the row is built from a stats row that
 *  may not have been computed yet. */
export interface MomentumRow {
  member: InsightsMember | null;
  stage: string | null;
  momentum: number | null;
  recent_engagement: number | null;
  prior_engagement: number | null;
  activity: number | null;
}

/** The two action lists: thank the one, check in on the other. Current members
 *  only — someone who has left is a win-back (group G), not a nudge. */
export interface MomentumBlock {
  warming: MomentumRow[];
  cooling: MomentumRow[];
}

/** One row of the 30-day leaderboard. */
export interface ContributorRow {
  member: InsightsMember | null;
  posts: number;
  comments: number;
  activity: number;
  upvotes_received: number;
}

/** §5.2: ONE definition of "top posts" — windowed and staff-excluded. The
 *  unwindowed, staff-inclusive lists of the same name on
 *  `/admin/analytics/community` do not survive into v1. */
export interface TopPostsBlock {
  by_upvotes: InsightsPost[];
  by_comments: InsightsPost[];
}

/** One comment in `top_comments`. `post` is nested rather than flattened into
 *  `post_id`/`post_title` (§2.2 reuses one post ref), and `excerpt` is the server's
 *  truncation — the client never re-truncates, so the two surfaces cannot disagree
 *  about where the cut falls. */
export interface InsightsComment {
  id: number;
  external_id: string;
  post_id: number;
  parent_id: number | null;
  depth: number;
  pinned: boolean;
  author: InsightsMember | null;
  upvotes: number | null;
  replies: number;
  excerpt: string;
  created_at: string | null;
  url: string | null;
  post: { id: number; title: string | null; slug: string | null; url: string | null } | null;
}

/** The comment half of the Engagement page's "Top content" group. Split by the two
 *  things a comment can win on, exactly as `top_posts` is. */
export interface TopCommentsBlock {
  by_upvotes: InsightsComment[];
  by_replies: InsightsComment[];
}

/** `ai_headline` — the assistant's counters. `AiInsights.headline` carries every one
 *  of these except `last_conversation_at`, which v1 adds. */
export interface AiHeadlineBlock {
  conversations: number;
  last_conversation_at?: string | null;
  questions: number;
  askers: number;
  feedback_up: number;
  feedback_down: number;
  rated: number;
  positive_rate: number | null;
  answers: number;
  sourced: number;
  sourced_rate: number | null;
  avg_tool_rounds: number | null;
  returning_askers: number;
}

export interface AiWeek {
  week: string;
  conversations: number;
  questions: number;
  askers?: number;
}

/** One person who talked to the assistant. `member` is the community member we
 *  resolved them to, or null — `name`/`handle` are what the chat widget recorded, and
 *  are what an anonymous asker is displayed as. */
export interface AiAskerRow {
  key: string;
  member: InsightsMember | null;
  external_id: string | null;
  handle: string | null;
  name: string | null;
  conversations: number;
  questions: number;
  last_message_at: string | null;
  returning: boolean;
}

/** A question the assistant answered without citing stored content — the content-gap
 *  list. `asker_name` is a display string, not a member reference. */
export interface AiUngroundedRow {
  conversation_id: number;
  text: string;
  asker_name: string | null;
  created_at: string | null;
}

/** The live contributor census: how many members hold each role right now, and the
 *  momentum-stage split. Built off the same `window_role_map` as `scatter` and
 *  `role_volume`, so a dot can never disagree with a card. */
export interface CensusBlock {
  roles: Record<string, number>;
  quiet_experts: number;
  stages: Record<string, number>;
}

/** The census's trail: each point is the community re-counted as of that date. The
 *  last stage point is pinned to the live census, so the trend ends on the real
 *  figure. `labels` are the x-axis tick labels, one per sample. */
export interface CensusSparkBlock {
  roles: Record<string, number[]>;
  stages: Record<string, number[]>;
  contributors: number[];
  labels: string[];
  // Roles are censused by calendar MONTH from the canonical `monthly_role_map` (the same
  // classifier the journey bar uses), so they carry their own month labels, distinct from
  // the fine `labels` the stage/contributor sparklines sample on.
  role_labels?: string[];
}

/** Cumulative posts + comments per role over the fixed 30-day window. Always
 *  carries a `departed` bucket so the per-role totals still sum to what the
 *  community wrote — content by members who have since left maps to no live role,
 *  and dropping it would leave the cards quietly adding up to less. */
export type RoleVolumeBlock = Record<string, { posts: number; comments: number }>;

export interface ScatterPoint {
  member: InsightsMember | null;
  activity: number;
  upvotes_received: number;
  value_per_contribution: number;
  stage: MemberStage;
  quadrant: string;
}

/** Up to 1,500 dots — `total` is how many there were before the cap. Ask for this
 *  block only where the plot is actually drawn (§5.2). */
export interface ScatterBlock {
  points: ScatterPoint[];
  median_activity: number;
  median_value: number;
  total: number;
}

/** Per-category post stats, plus the best/worst read the pages show as a headline.
 *  `best`/`worst` are already display strings (name, or `label <id>`). */
export interface LabelsBlock {
  items: LabelStat[];
  best: string | null;
  worst: string | null;
}

/** Weekly posting volume against per-post engagement, and their correlation:
 *  positive means the community wants more, negative that it saturates. */
export interface TimingBlock {
  overwhelm: OverwhelmWeek[];
  overwhelm_corr: number | null;
  spark_labels: string[];
}

/**
 * Every block this client knows how to read, all optional — a block is ABSENT
 * (not null) unless `?include=` named it, because the route runs
 * `response_model_exclude_unset=True`.
 *
 * The route serves twenty-five; the one not declared here is `moderation`, whose page
 * has not migrated yet. It is typed HERE, in this interface, when it moves; the block
 * list is the contract, and a second module for the same route would be exactly the
 * per-route wrapper sprawl v1 exists to end. The six `ai_*` blocks joined it when AI
 * Insights moved. `questions_headline` and `locked_demand` were listed here too until
 * the clustering pipeline was retired and the route stopped serving them.
 */
export interface InsightsBlocks {
  summary?: SummaryBlock;
  engagement?: EngagementBlock;
  concentration?: ConcentrationBlock;
  clock?: ClockCell[];
  trend?: TrendWeek[];
  heatmap?: HeatmapBlock;
  momentum?: MomentumBlock;
  top_contributors?: ContributorRow[];
  top_posts?: TopPostsBlock;
  top_comments?: TopCommentsBlock;
  ai_headline?: AiHeadlineBlock;
  ai_weekly?: AiWeek[];
  ai_sources?: AiSource[];
  ai_feedback?: AiFeedbackComment[];
  ai_askers?: AiAskerRow[];
  ai_ungrounded?: AiUngroundedRow[];
  census?: CensusBlock;
  census_spark?: CensusSparkBlock;
  role_volume?: RoleVolumeBlock;
  scatter?: ScatterBlock;
  cohorts?: CohortRow[];
  labels?: LabelsBlock;
  timing?: TimingBlock;
  revenue?: RevenueBlock;
}

/**
 * What the membership is worth, what of it is wobbling, and what already left.
 * Every money field is CENTS.
 *
 * MRR AND ARR ARE TWO METRICS, not one converted into the other. They are summed over
 * different populations — monthly plans and annual plans — and nothing on either side
 * of the wire multiplies or divides by 12. Someone who pays a year up front and cancels
 * the next day never paid you a monthly amount, and smearing their invoice across twelve
 * months would invent recurring revenue that never recurred.
 *
 * `paying` vs `priced` is not decoration. Skool sends the plan tier and the plan price
 * independently, so a community can have 78 paying members and no observed amounts at
 * all — rendering `live_cents` without checking `priced` prints "$0" under a community
 * that is plainly being paid. `<RevenueTiles>` is the only place that judgement lives.
 */
export interface RevenueBlock {
  /** Monthly plans only. NOT annual ÷ 12 — see the doc comment above. */
  mrr_cents: number;
  /** Annual plans only. NOT monthly × 12. */
  arr_cents: number;
  /** Everything ever collected, all-time, including members who have left. */
  lifetime_cents: number;
  lifetime_members: number;
  /** Cancelled and running out the period — a real end date. */
  /** Everyone on their way out — cancelled AND declined. Matches the board's Cancelled
   *  column and the `cancelled` segment, so the figure and its own link agree. */
  cancelling_mrr_cents: number;
  cancelling_arr_cents: number;
  /** The recoverable half of the above: a card that failed, not a decision. */
  declined_mrr_cents: number;
  declined_arr_cents: number;
  declined_members: number;
  /** Was engaged, activity fell away — a forecast, not a fact. */
  slipping_mrr_cents: number;
  slipping_arr_cents: number;
  churned_mrr_cents: number;
  churned_arr_cents: number;
  paying: number;
  priced: number;
  monthly_members: number;
  annual_members: number;
  /** Cancelled, running out their period — a real end date. */
  leaving_members: number;
  /** Engaged payers whose activity fell away — a forecast. */
  slipping_members: number;
  /** Paying and not contributing right now.
   *
   *  Mutually exclusive with `leaving_members` and `slipping_members`; together with
   *  `engaged_members` the four sum to `paying` exactly.
   *
   *  The exclusivity is produced by precedence, not by the conditions being disjoint:
   *  8 of those 87 satisfy more than one test (cancelled AND since gone quiet), and the
   *  server's `if/elif/elif` files them under cancelling because the end date is the more
   *  actionable fact. Turning that chain into three independent `if`s would double-count. */
  quiet_members: number;
  quiet_mrr_cents: number;
  quiet_arr_cents: number;
  /** Paying, present, contributing, not cancelling. With the three above these four DO
   *  cover `paying`, which is what lets the risk bar be a full partition. */
  engaged_members: number;
  engaged_mrr_cents: number;
  engaged_arr_cents: number;
  churned_members: number;
  churned_monthly_members: number;
  churned_annual_members: number;
  churned_unpriced_members: number;
  currencies: string[];
}

/** The envelope: the community reference every community-scoped response carries
 *  once at the top level, when the aggregates were last rebuilt, and the blocks. */
export interface Insights extends InsightsBlocks {
  community: CommunityRef;
  last_updated: string | null;
}

export type InsightBlock = keyof InsightsBlocks;

/** The response with the blocks you asked for made NON-OPTIONAL. `fetchInsights`
 *  is generic over its `include` list, so a page that asked for `census` reads
 *  `data.census.roles` without a `?.` and a page that did not ask for `scatter`
 *  cannot read `data.scatter` at all. The type mirrors the wire contract instead of
 *  describing it loosely. */
export type InsightsWith<K extends InsightBlock> = Insights & Required<Pick<InsightsBlocks, K>>;

// ---------------------------------------------------------------------------
// The call
// ---------------------------------------------------------------------------

/** The default analytics window: a year of history for the heatmap, the trend and
 *  the clock. Stated once — §1.4's point is that every analytics read sends the
 *  same cache-key-stable shorthand. */
export const YEAR = '365d';

/** `?last=` for a `PeriodSelector` value. `WINDOWS` (`lib/periods.ts`) spells "all
 *  time" as `days: 0`; v1 spells it `all` (§1.4), which means the epoch. */
export function lastFor(days: number): string {
  return days > 0 ? `${days}d` : 'all';
}

export interface InsightsQuery {
  /** `30d` | `52w` | `365d` | `all`. Defaults to `YEAR`. Never send `since` from a
   *  page: an instant defeats the server's per-window cache. */
  last?: string;
  /** How many rows the top-N blocks (`top_contributors`, `top_posts`, `momentum`)
   *  return. Server default 10, max 50. */
  limit?: number;
}

/**
 * One request for as many aggregate blocks as the page renders — and no more.
 *
 *     const data = await fetchInsights(skoolId, OVERVIEW_BLOCKS, { last: YEAR });
 *     data.census.roles;      // typed, non-optional: it was in the list
 *
 * The blocks go out as a REPEATED `?include=` parameter (§1.6), which is what the
 * server validates against its block list; an unknown name is a 400
 * `unknown_include` naming it, never a silent drop.
 */
export function fetchInsights<K extends InsightBlock>(
  skoolId: number,
  include: readonly K[],
  opts: InsightsQuery = {},
): Promise<InsightsWith<K>> {
  return apiGet<InsightsWith<K>>(community(skoolId, '/insights'), {
    include: [...include],
    last: opts.last ?? YEAR,
    limit: opts.limit,
  });
}

// ---------------------------------------------------------------------------
// What each page asks for
// ---------------------------------------------------------------------------

/**
 * Overview — §4.3's one request, replacing three (`/admin/analytics/community`,
 * `/admin/analytics/content` and `/admin/analytics/contributors`), one of which
 * was fetched for nine numbers (§5.2) and two of which each computed and shipped
 * their own copy of the same clock grid.
 *
 * `timing` is on the list and doc §4.3's sample query does not name it: the page's
 * "What lands" card renders the Flooding Score, which is `timing.overwhelm_corr`,
 * and it came off `/admin/analytics/content` before. It is one block on a request
 * that is already being made — still one request — and dropping it would have
 * silently blanked a rendered widget.
 *
 * `census` / `census_spark` were on this list and are NOT any more. They fed two
 * `SegmentDonut`s that the `<ContributorsEngagement>` card replaced; that card owns
 * a period control, so its blocks cannot ride this request's fixed `last=365d` —
 * see {@link CONTRIBUTORS_CARD_BLOCKS}. Nothing else on the Overview read them, so
 * leaving them here would have been a census computed on every load and rendered
 * nowhere.
 *
 * `concentration` came off for the same reason and is now requested by NO page. Its
 * only reader was the generated stat sentence under the Overview heading, which was
 * deleted; the block itself is still served (`?include=concentration`), it is simply
 * no longer asked for. Removing it here is what stops the server computing a
 * top-decile distribution on every Overview load for nobody.
 */
export const OVERVIEW_BLOCKS = [
  'summary',
  // Window-independent: `revenue` reads the roster as it stands, so it ignores `last`
  // entirely. It rides in this request rather than its own because the Overview already
  // makes exactly one.
  'revenue',
  'engagement',
  'clock',
  'trend',
  'heatmap',
  'momentum',
  'top_contributors',
  'top_posts',
  'labels',
  'timing',
] as const satisfies readonly InsightBlock[];

/**
 * The blocks `<ContributorsEngagement>` renders — the role census, its sparkline and
 * the per-role post/comment volume.
 *
 * Named once because TWO pages mount that card. The Contributors page folds them
 * into its own request (below); the Overview asks for exactly these, beside its
 * `last=365d` page request, because the card carries a `PeriodSelector` and the
 * Overview's window is load-bearing for the heatmap, trend, clock, labels and top
 * posts — turning the card's dial may not re-window "Daily activity · last 12
 * months" underneath it. Same reason `fetchTopContent` is its own call.
 *
 * Only `census_spark` actually moves with the period: `census` and `role_volume`
 * classify over the fixed 30-day contributor window whatever `last` says, which is
 * what stops a card here from disagreeing with a badge elsewhere (§3.1 H).
 */
export const CONTRIBUTORS_CARD_BLOCKS = [
  'census',
  'census_spark',
  'role_volume',
] as const satisfies readonly InsightBlock[];

/** Contributors — the card's three blocks, plus the two heavy ones only this page
 *  draws. */
export const CONTRIBUTOR_BLOCKS = [
  ...CONTRIBUTORS_CARD_BLOCKS,
  'scatter',
  'cohorts',
] as const satisfies readonly InsightBlock[];

/** Engagement — `labels` and `timing` are what `/admin/analytics/content`
 *  returned; `clock` is the third thing that response carried ("best time to
 *  post"), and it is its own block here rather than a field of two others. */
/**
 * The Engagement page's "Top content" widget — the last legacy analytics read to move.
 *
 * TWO REASONS IT IS ITS OWN CALL rather than blocks folded into `ENGAGEMENT_BLOCKS`:
 * the widget owns its OWN period selector, independent of the page's, so it cannot ride
 * the page request's `?last=`; and it re-fetches on that selector alone, where the rest
 * of the page does not.
 *
 * A REAL NUMBERS CHANGE, stated rather than hidden. `top_posts_by_upvotes` meant two
 * different things on the legacy surface: staff-EXCLUDED and windowed on
 * `/admin/analytics/top-content`, unfiltered and all-time on `/admin/analytics/community`
 * (doc 04 §4, "same list, two populations, one key name"). v1 keeps ONE definition — the
 * windowed, staff-excluded one, i.e. what this widget already showed — so this page's
 * figures are unchanged and it is the Overview's old copy that no longer exists.
 *
 * Returns the legacy `TopContent` shape for the same reason `fetchMemberAnalytics`
 * does: `<TopContent>` renders four lists off it and the mapping is better written once
 * here than threaded through the component.
 */
export async function fetchTopContent(skoolId: number, days = 30): Promise<TopContent> {
  const data = await fetchInsights(skoolId, ['top_posts', 'top_comments'] as const, {
    last: days === 0 ? 'all' : `${days}d`,
  });
  const post = (p: InsightsPost): PostBrief => ({
    id: p.id,
    title: p.title,
    slug: p.slug,
    author: toAuthor(p.author),
    upvotes: p.upvotes,
    comment_count: p.comment_count,
    created_at: p.created_at,
  });
  // Flatten §2.2's nested post ref back to the pair the legacy row carried.
  const comment = (c: InsightsComment): CommentBrief => ({
    id: c.id,
    excerpt: c.excerpt,
    post_id: c.post_id,
    post_title: c.post?.title ?? null,
    author: toAuthor(c.author),
    upvotes: c.upvotes,
    replies: c.replies,
    created_at: c.created_at,
  });
  return {
    last_updated: data.last_updated,
    window_days: days,
    top_posts_by_upvotes: data.top_posts.by_upvotes.map(post),
    top_posts_by_comments: data.top_posts.by_comments.map(post),
    top_comments_by_upvotes: data.top_comments.by_upvotes.map(comment),
    top_comments_by_replies: data.top_comments.by_replies.map(comment),
  };
}

export const ENGAGEMENT_BLOCKS = [
  'labels',
  'timing',
  'clock',
] as const satisfies readonly InsightBlock[];
